import { Link } from "react-router-dom"
import { IreneLogo } from "@/components/irene-logo"
import { ArrowRight, Search, FileText, BarChart3, FolderOpen, Sparkles, Mail } from "lucide-react"

export default function Home() {
  return (
    <div className="min-h-screen bg-card">
      {/* Nav */}
      <header className="flex items-center justify-between px-6 py-5 md:px-12 lg:px-20">
        <div className="flex items-center gap-2.5">
          <IreneLogo size={32} />
          <span className="text-lg font-bold tracking-tight text-foreground">Irene</span>
        </div>
        <nav className="hidden items-center gap-8 md:flex">
          <a href="#features" className="text-sm text-muted-foreground transition-colors hover:text-foreground">Features</a>
          <a href="#how" className="text-sm text-muted-foreground transition-colors hover:text-foreground">How it works</a>
          <a href="#pricing" className="text-sm text-muted-foreground transition-colors hover:text-foreground">Pricing</a>
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            Sign in
          </Link>
          <Link
            to="/onboarding"
            className="rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-card transition-opacity hover:opacity-90"
          >
            Get Started
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="flex flex-col items-center px-6 pb-20 pt-16 text-center md:pb-28 md:pt-24">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-1.5">
          <span className="h-2 w-2 rounded-full bg-primary" />
          <span className="text-xs font-medium text-muted-foreground">Now with AI-powered investor search</span>
          <ArrowRight className="h-3 w-3 text-muted-foreground" />
        </div>

        <h1 className="max-w-3xl text-balance text-4xl font-bold leading-tight tracking-tight text-foreground md:text-6xl md:leading-[1.1]">
          Stop chasing investors.<br />Let AI do the work.
        </h1>

        <p className="mt-6 max-w-xl text-pretty text-base leading-relaxed text-muted-foreground md:text-lg">
          Irene finds the right investors for your startup, writes updates
          they actually want to read, and tells you who opened them.
        </p>

        <div className="mt-10 flex items-center gap-4">
          <Link
            to="/onboarding"
            className="flex items-center gap-2 rounded-full bg-foreground px-7 py-3.5 text-sm font-medium text-card transition-opacity hover:opacity-90"
          >
            Try Irene free
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/dashboard"
            className="rounded-full border border-border bg-card px-7 py-3.5 text-sm font-medium text-foreground transition-colors hover:bg-background"
          >
            See the dashboard
          </Link>
        </div>
      </section>

      {/* Social proof numbers */}
      <section className="border-y border-border bg-background">
        <div className="mx-auto grid max-w-5xl grid-cols-2 md:grid-cols-4">
          {[
            { number: "10x", text: "faster investor updates" },
            { number: "500+", text: "investors in database" },
            { number: "85%", text: "open rate on updates" },
            { number: "3 min", text: "to analyze a deck" },
          ].map((stat) => (
            <div key={stat.text} className="flex flex-col items-center gap-1 border-r border-border px-6 py-8 last:border-r-0 [&:nth-child(2)]:border-r-0 md:[&:nth-child(2)]:border-r">
              <span className="text-2xl font-bold text-foreground md:text-3xl">{stat.number}</span>
              <span className="text-center text-xs text-muted-foreground">{stat.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Features - simple cards */}
      <section id="features" className="px-6 py-20 md:py-28">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center text-sm font-semibold uppercase tracking-widest text-primary">What Irene does</h2>
          <p className="mx-auto mt-3 max-w-lg text-balance text-center text-2xl font-bold text-foreground md:text-3xl">
            Everything you need to raise money, minus the busywork
          </p>

          <div className="mt-14 grid gap-4 md:grid-cols-3">
            {[
              {
                icon: Search,
                title: "Find investors",
                desc: "Tell Irene about your startup. She searches the internet and finds investors who actually fund companies like yours.",
              },
              {
                icon: Mail,
                title: "Write updates",
                desc: "Irene writes monthly investor updates in your voice. Pick a tone -- data-heavy, visionary, or milestone-based. Hit send.",
              },
              {
                icon: BarChart3,
                title: "Track engagement",
                desc: "See who opened your update, how long they read, and what they clicked. Know which investors are warm before you call.",
              },
              {
                icon: FileText,
                title: "Analyze your deck",
                desc: "Paste your pitch deck. Irene tells you what's strong, what's weak, and what investors will ask about. In 3 minutes.",
              },
              {
                icon: FolderOpen,
                title: "Share a deal room",
                desc: "One link. All your docs. Track exactly who viewed what, for how long. No more \"did you get my email?\"",
              },
              {
                icon: Sparkles,
                title: "Get AI advice",
                desc: "Ask Irene anything about an investor -- their portfolio, what they care about, how to approach them. She knows.",
              },
            ].map((feature) => (
              <div
                key={feature.title}
                className="flex flex-col rounded-2xl border border-border bg-background p-6"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted">
                  <feature.icon className="h-5 w-5 text-foreground" />
                </div>
                <h3 className="mt-4 text-base font-semibold text-foreground">{feature.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-y border-border bg-background px-6 py-20 md:py-28">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-center text-sm font-semibold uppercase tracking-widest text-primary">How it works</h2>
          <p className="mx-auto mt-3 max-w-md text-balance text-center text-2xl font-bold text-foreground md:text-3xl">
            Three steps. That's it.
          </p>

          <div className="mt-14 flex flex-col gap-10">
            {[
              {
                step: "1",
                title: "Tell Irene about your startup",
                desc: "Company name, what you do, how much you're raising, what stage you're at. Takes 2 minutes.",
              },
              {
                step: "2",
                title: "Irene goes to work",
                desc: "She searches the internet for matching investors, builds profiles, and drafts your first update. You review and tweak.",
              },
              {
                step: "3",
                title: "Send, track, close",
                desc: "Send updates with one click. Share your deal room. See exactly who's interested. Focus your time on the investors who care.",
              },
            ].map((item) => (
              <div key={item.step} className="flex gap-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-foreground text-sm font-bold text-card">
                  {item.step}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="px-6 py-20 md:py-28">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-center text-sm font-semibold uppercase tracking-widest text-primary">Pricing</h2>
          <p className="mx-auto mt-3 max-w-md text-balance text-center text-2xl font-bold text-foreground md:text-3xl">
            Simple pricing. Cancel anytime.
          </p>

          <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                name: "Free",
                price: "$0",
                desc: "Get started",
                features: ["25 investors", "1 deal room", "3 AI updates / month", "1 deck analysis / month"],
                cta: "Get started free",
                highlighted: false,
              },
              {
                name: "Starter",
                price: "$49",
                desc: "Early-stage founders",
                features: ["100 investors", "2 deal rooms", "20 AI updates / month", "5 deck analyses / month"],
                cta: "Start free trial",
                highlighted: false,
              },
              {
                name: "Growth",
                price: "$149",
                desc: "Actively fundraising",
                features: ["500 investors", "5 deal rooms", "Unlimited AI", "AI investor search"],
                cta: "Start free trial",
                highlighted: true,
              },
              {
                name: "Scale",
                price: "$349",
                desc: "Established startups",
                features: ["Unlimited everything", "Custom integrations", "API access", "Dedicated support"],
                cta: "Start free trial",
                highlighted: false,
              },
            ].map((plan) => (
              <div
                key={plan.name}
                className={`flex flex-col rounded-2xl border p-6 ${
                  plan.highlighted
                    ? "border-foreground bg-foreground text-card"
                    : "border-border bg-background text-foreground"
                }`}
              >
                <span className={`text-xs font-semibold uppercase tracking-wider ${plan.highlighted ? "text-card/60" : "text-muted-foreground"}`}>
                  {plan.name}
                </span>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-3xl font-bold">{plan.price}</span>
                  <span className={`text-sm ${plan.highlighted ? "text-card/60" : "text-muted-foreground"}`}>/mo</span>
                </div>
                <p className={`mt-1 text-sm ${plan.highlighted ? "text-card/70" : "text-muted-foreground"}`}>{plan.desc}</p>
                <ul className="mt-5 flex flex-col gap-2.5">
                  {plan.features.map((f) => (
                    <li key={f} className={`flex items-center gap-2 text-sm ${plan.highlighted ? "text-card/80" : "text-muted-foreground"}`}>
                      <span className={`h-1 w-1 rounded-full ${plan.highlighted ? "bg-card/50" : "bg-muted-foreground/50"}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/onboarding"
                  className={`mt-6 rounded-full py-2.5 text-center text-sm font-medium transition-opacity hover:opacity-90 ${
                    plan.highlighted
                      ? "bg-card text-foreground"
                      : "bg-foreground text-card"
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-border bg-background px-6 py-20 text-center md:py-28">
        <h2 className="text-balance text-2xl font-bold text-foreground md:text-3xl">
          Fundraising is hard enough.<br />The admin work shouldn't be.
        </h2>
        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
          Join founders who spend less time on investor emails and more time building their company.
        </p>
        <Link
          to="/onboarding"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
        >
          Get started for free
          <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      {/* Footer */}
      <footer className="border-t border-border px-6 py-8">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-4 md:flex-row">
          <div className="flex items-center gap-2">
            <IreneLogo size={24} />
            <span className="text-sm font-semibold text-foreground">Irene</span>
          </div>
          <p className="text-xs text-muted-foreground">Built with AI. For founders, by founders.</p>
        </div>
      </footer>
    </div>
  )
}
